import type { Route } from './+types/postcodes';
import type { SuburbPostcode } from '@/components/sections/postcode-list';
import type { ValueItem } from '@/components/sections/value-proposition';
import { CtaBanner } from '@/components/sections/cta-banner';
import { HeroSection } from '@/components/sections/hero-section';
import { PostcodeListSection } from '@/components/sections/postcode-list';
import { ValueProposition } from '@/components/sections/value-proposition';
import { inheritRootLdJson } from '@/utils/schema';

const KEY_POSTCODES: Array<ValueItem> = [
  { title: '4610', description: 'Kingaroy and surrounds — the main hub' },
  { title: '4605', description: 'Murgon and surrounds' },
  { title: '4606', description: 'Wondai and surrounds' },
  { title: '4608', description: 'Wooroolin / Tingoora area' },
  { title: '4611', description: 'Mondure area' },
  { title: '4612', description: 'Hivesville area' },
  { title: '4613', description: 'Proston / Boondooma area' },
  { title: '4615', description: 'Nanango and surrounds' },
  { title: '4306', description: 'Blackbutt area (southern edge of the region)' },
];

const SUBURB_POSTCODES: Array<SuburbPostcode> = [
  { suburb: 'Abbeywood', postcode: '4613' },
  { suburb: 'Alice Creek', postcode: '4610' },
  { suburb: 'Ballogie', postcode: '4610' },
  { suburb: 'Barker Creek Flat', postcode: '4615' },
  { suburb: 'Barlil', postcode: '4605' },
  { suburb: 'Benair', postcode: '4610' },
  { suburb: 'Benarkin North', postcode: '4306' },
  { suburb: 'Blackbutt', postcode: '4306' },
  { suburb: 'Blackbutt North', postcode: '4306' },
  { suburb: 'Blackbutt South', postcode: '4306' },
  { suburb: 'Booie', postcode: '4610' },
  { suburb: 'Boondooma', postcode: '4613' },
  { suburb: 'Boyneside', postcode: '4610' },
  { suburb: 'Brigooda', postcode: '4613' },
  { suburb: 'Brooklands', postcode: '4615' },
  { suburb: 'Bullcamp', postcode: '4615' },
  { suburb: 'Bunya Mountains', postcode: '4405' },
  { suburb: 'Byee', postcode: '4605' },
  { suburb: 'Chahpingah', postcode: '4610' },
  { suburb: 'Charlestown', postcode: '4608' },
  { suburb: 'Chelmsford', postcode: '4606' },
  { suburb: 'Cloyna', postcode: '4605' },
  { suburb: 'Cobbs Hill', postcode: '4605' },
  { suburb: 'Coolabunia', postcode: '4610' },
  { suburb: 'Crawford', postcode: '4610' },
  { suburb: 'Cushnie', postcode: '4610' },
  { suburb: 'Durong', postcode: '4610' },
  { suburb: 'Goodger', postcode: '4610' },
  { suburb: 'Goomeri', postcode: '4601' },
  { suburb: 'Gordonbrook', postcode: '4610' },
  { suburb: 'Hivesville', postcode: '4612' },
  { suburb: 'Kingaroy', postcode: '4610' },
  { suburb: 'Kumbia', postcode: '4610' },
  { suburb: 'Maidenwell', postcode: '4615' },
  { suburb: 'Memerambi', postcode: '4610' },
  { suburb: 'Moffatdale', postcode: '4605' },
  { suburb: 'Mondure', postcode: '4611' },
  { suburb: 'Mount Mowbullan', postcode: '4610' },
  { suburb: 'Murgon', postcode: '4605' },
  { suburb: 'Nanango', postcode: '4615' },
  { suburb: 'Proston', postcode: '4613' },
  { suburb: 'Ringtail Creek', postcode: '4606' },
  { suburb: 'Sheep Station Creek', postcode: '4606' },
  { suburb: 'Sunny Nook', postcode: '4605' },
  { suburb: 'Tablelands', postcode: '4605' },
  { suburb: 'Taabinga', postcode: '4610' },
  { suburb: 'Tingoora', postcode: '4608' },
  { suburb: 'Wattle Grove', postcode: '4610' },
  { suburb: 'Wheatlands', postcode: '4606' },
  { suburb: 'Windera', postcode: '4605' },
  { suburb: 'Wondai', postcode: '4606' },
  { suburb: 'Wooroolin', postcode: '4608' },
  { suburb: 'Wooroonden', postcode: '4605' },
  { suburb: 'Wyalla', postcode: '4615' },
];

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'Suburbs & Postcodes | Kingaroy & South Burnett | Meet Real Estate' },
    {
      name: 'description',
      content:
        'A full A-Z reference of suburb and locality postcodes across the South Burnett region, including Kingaroy, Murgon, Wondai, Nanango, Proston and Blackbutt.',
    },
  ];
}

export default function Postcodes() {
  return (
    <main>
      <HeroSection
        eyebrow="Local Area"
        heading="South Burnett & Kingaroy — Suburbs & Postcodes"
        description="A handy reference for every suburb and locality across the South Burnett region, from Kingaroy to Blackbutt."
        primaryCta={{ label: 'Get in Touch', to: '#contact' }}
      />

      <ValueProposition
        id="key-postcodes"
        className="scroll-mt-20"
        eyebrow="Quick Reference"
        heading="Key Town Postcodes"
        description="Many of the smaller rural localities share a postcode with their nearest town — these are the main hubs across the region."
        align="center"
        items={KEY_POSTCODES}
      />

      <PostcodeListSection
        id="all-suburbs"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Full List"
        heading="All Suburbs, A-Z"
        description="99 localities span the South Burnett region — many are small rural areas sharing a postcode with a larger neighbouring town."
        entries={SUBURB_POSTCODES}
      />

      <CtaBanner
        id="contact"
        className="scroll-mt-20"
        heading="Looking to Buy or Sell in the South Burnett?"
        description="Wherever your property sits across the region, a confidential conversation costs you nothing."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
        secondaryCta={{ label: 'Call Lucio', to: 'tel:0493035628' }}
        contactName="Lucio Passos"
        contactTitle="Sales Agent, Meet Real Estate"
        contactLinks={[
          { label: 'Mobile', value: '0493 035 628', href: 'tel:0493035628' },
          { label: 'Office', value: '07 3355 7777', href: 'tel:0733557777' },
          { label: 'Email', value: 'lucio@meetrealestate.com.au', href: 'mailto:lucio@meetrealestate.com.au' },
          { label: 'Website', value: 'meetrealestate.com.au', href: 'https://meetrealestate.com.au' },
        ]}
      />
    </main>
  );
}
